import { type Dispatch, type SetStateAction, useContext } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { type SubstantiationsFilters } from 'components/hooks/substantiations/useGetSubstantiations'
import { Button } from 'slab'
import { type BrokersBroker } from 'utils/web_api.schemas'

type SubstantiationsQuickFiltersProps = {
  filters: SubstantiationsFilters
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
}

// The queries below provide quick filters for common substantiation triaging scenarios.
function setWaitingForReview (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    review_statuses: ['waiting_for_review'],
  }))
}

function setApproved (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    review_statuses: ['approved'],
  }))
}

function setRejected (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    review_statuses: ['rejected'],
  }))
}

function setAssignedToMe (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters(() => ({
    broker_ids: (currentBroker ? [currentBroker.id] : []),
  }))
}

function setUnassigned (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters(() => ({
    broker_ids: ['unassigned'],
  }))
}

function setVipEmployerFilters (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    vip_employer: 'true',
  }))
}

function setHasReceiptsFilter (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    has_receipts: 'true',
  }))
}

function setUnitCardTransactionFilter (
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    payment_method_unit_card: 'true',
  }))
}

const SubstantiationsQuickFilters = ({
  setFilters,
}: SubstantiationsQuickFiltersProps) => {
  const { currentBroker } = useContext(BrokerContext)

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setWaitingForReview(setFilters)}
      >
        Waiting For Review
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setApproved(setFilters)}
      >
        Approved
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setRejected(setFilters)}
      >
        Rejected
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setAssignedToMe(setFilters, currentBroker)}
      >
        Assigned To Me
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnassigned(setFilters)}
      >
        Unassigned
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setVipEmployerFilters(setFilters)}
      >
        VIP Employers
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setHasReceiptsFilter(setFilters)}
      >
        Has Receipts
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnitCardTransactionFilter(setFilters)}
      >
        Unit Card Transaction
      </Button>
    </div>
  )
}

export default SubstantiationsQuickFilters
